import type React from "react";
import { useCallback, useEffect } from "react";

import { faCircleQuestion, faFile, faGraduationCap, faPowerOff, faUser } from "@fortawesome/pro-regular-svg-icons";
import { faGraduationCap as faGraduationCapSolid, faUser as faUserSolid } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Collapse } from "@mui/material";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { useAppConfigContext } from "contexts/AppConfigContext";
import { useProfileContext } from "contexts/ProfileContext";
import { ListItem } from "shared/NavBar/components/Menu";
import { MarketTypes } from "types";
import { signOutUser } from "utils/auth";
import { getResourcesLink, getSupportLink } from "utils/market";
import { isRouteAllowed } from "utils/profile/profileHelper";

interface Props {
  open: boolean;
  onClose: () => void;
}

export const NavBarDropdown: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { navBarCollapsed } = useAppConfigContext();
  const { profile, setProfile } = useProfileContext();

  const ifAllowed = (route: string) => !!(profile && isRouteAllowed(profile, route));

  const handleLogoutClick = async () => {
    await signOutUser();
    setProfile(undefined);
  };

  const clickAway = useCallback(() => {
    if (open) {
      onClose();
    }
  }, [open, onClose]);

  useEffect(() => {
    document.addEventListener("click", clickAway);
    return () => {
      document.removeEventListener("click", clickAway);
    };
  }, [clickAway]);

  const resourcesLink = getResourcesLink(profile?.market);
  const supportLink = getSupportLink(profile?.market);
  const showFaq = !profile?.therapist_profile?.branded_health_care_provider?.feature_flags?.includes("HEP_ENABLED");

  return (
    <Container>
      <Collapse in={open}>
        <DropdownList data-testid="dropdown-list" $navBarCollapsed={navBarCollapsed}>
          {profile?.state === "ready" && (
            <>
              {ifAllowed("/settings") && (
                <ListItem
                  to="/settings"
                  label={t("dropdown.profile")}
                  icon={<FontAwesomeIcon icon={faUser} />}
                  iconActive={<FontAwesomeIcon icon={faUserSolid} />}
                  dataTestId="dropdown-item-settings"
                  showBackgroundOnActive
                />
              )}
              {resourcesLink && (
                <ListItem
                  to={resourcesLink}
                  label={t("dropdown.tutorial")}
                  icon={<FontAwesomeIcon icon={faFile} />}
                  dataTestId="dropdown-item-resources"
                />
              )}

              {ifAllowed("/wiki") && profile?.market === MarketTypes.US && !profile?.health_care_organization && (
                <ListItem
                  to="/wiki"
                  label={t("dropdown.wiki")}
                  icon={<FontAwesomeIcon icon={faGraduationCap} />}
                  iconActive={<FontAwesomeIcon icon={faGraduationCapSolid} />}
                  dataTestId="dropdown-item-wiki"
                  showBackgroundOnActive
                />
              )}
            </>
          )}

          {showFaq && (
            <ListItem
              to={supportLink}
              label={t("dropdown.support")}
              icon={<FontAwesomeIcon icon={faCircleQuestion} />}
              dataTestId="dropdown-item-support"
              showBackgroundOnActive
            />
          )}

          <ListItem
            to=""
            label={t("dropdown.logout")}
            icon={<FontAwesomeIcon icon={faPowerOff} />}
            dataTestId="dropdown-item-logout"
            onClick={handleLogoutClick}
          />
        </DropdownList>
      </Collapse>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownList = styled.ul<{ $navBarCollapsed: boolean | undefined }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  list-style: none;
  justify-content: flex-start;
  gap: 8px;
  padding: ${props => (props.$navBarCollapsed ? "8px 4px" : "8px")};
  margin: 0;
  background-color: ${props => props.theme.colors.white};
  z-index: 1;
  box-sizing: border-box;
`;
